
export class extendedRaterFile {

    extendedRaterType: any = Helpers.brandDetails?.extended_rater_type;
    extendedRaterMapper: any = Helpers.brandDetails?.extended_rating_mapper;

    /**
     * Maps and combines rating data based on dynamic extended rater mappings.
     * Modified By: Manish Kumar
     * Date: 15-05-2025
     * @param {Array} ratingsData - Array of raw rating data (each with value, count).
     * @param {Object} question - Question object that contains main_rater_type.
     * @returns {Array} - Processed rating data with merged values and percentages.
     */
    getDynamicMappingExtendedRatingData(ratingsData: any[], question: any): any[] {
        // Get the specific value map for this question and rater type
        const valueMapper = this.extendedRaterMapper[question.main_rater_type][this.extendedRaterType];

        // Step 1: Transform the data and calculate total response count
        let totalResponse = 0;
        const mappedRatings = ratingsData.map(ratingData => {
            totalResponse += ratingData.count;
            return this.getNewDistributionForExtendedRaterType(ratingData, valueMapper);
        });

        // Step 2: Combine ratings with same value and calculate percentages
        return this.combineCommonDistributionValueForExtendedRaterType(mappedRatings, totalResponse);
    }

    /**
     * Returns a new rating object with its value mapped using the extended rater mapper.
     *
     * @param {Object} rating - The original rating object.
     * @param {Object} extendedRaterMapper - A mapping object to convert the rating value.
     * @returns {Object} - A new rating object with the updated value.
     */
    getNewDistributionForExtendedRaterType(rating, extendedRaterMapper) {
        return {
            ...rating, // Spread to clone the original rating object
            value: extendedRaterMapper[rating.value] // Replace 'value' with mapped value
        };
    }

    /**
     * Merges distribution entries with the same value by summing their counts,
     * then calculates each entry's percentage based on the total number of responses.
     *
     * @param {Array} options - Array of distribution objects (each with value, count).
     * @param {number} totalResponse - Total number of responses used to calculate percentages.
     * @returns {Array} - A new array with combined counts and calculated percentages.
     */
    combineCommonDistributionValueForExtendedRaterType(options: any[], totalResponse: number): any[] {
        if (!Array.isArray(options) || options.length === 0) return [];

        // Step 1: Combine entries with the same 'value' by summing their 'count'
        const combined = options.reduce((acc: any[], curr: any) => {
            const existing = acc.find(item => item.value === curr.value);
            if (existing) {
                existing.count += curr.count;
            } else {
                acc.push({ ...curr }); // Clone to prevent mutation
            }
            return acc;
        }, []);

        // Step 2: Calculate percentage for each entry
        combined.forEach(item => {
            item.percentage = this.getPercentage(item.count, totalResponse);
        });

        return combined;
    }

    /**
     * Calculates the percentage representation of a value out of a total.
     *
     * @param {number} sum - The part value.
     * @param {number} totalResponse - The total value.
     * @returns {string} - Percentage string (e.g., "25.0%").
     */
    getPercentage(sum: number, totalResponse: number): string {
        if (totalResponse <= 0) return '0%';
        const percentage = ((sum || 0) / totalResponse) * 100;
        return `${percentage.toFixed(1)}%`;
    }

    /**
     * Get the specific value map for this question and rater type.
     * 
     * @param primaryRating - Actual rating value.
     * @param question - Question object that contains main_rater_type.
     * @returns - Mapped rating value.
     */

    getExtendedRaterMappingPrimaryRating(primaryRating, question) {
        // Get the specific value map for this question and rater type
        const valueMapper = this.extendedRaterMapper[question.main_rater_type][this.extendedRaterType];
        return valueMapper[primaryRating] || primaryRating;
    }

    /**
     * Maps the rating value and updates its label/text based on the extended rater type.
     *
     * @param {Object} rating - The original rating object containing `value` and `label`.
     * @param {string} main_rater_type - The main rater type used to retrieve the appropriate value mapper.
     * @returns {Object} A new rating object with updated `value`, `label`, and `text`.
     */
    getNewOptionsForExtendedRaterType(rating, main_rater_type) {
        const { value: originalValue, label: originalLabel } = rating;

        // Retrieve the value mapping for the given main rater type and extended rater type
        const valueMapper = this.extendedRaterMapper[main_rater_type]?.[this.extendedRaterType];
        const mappedValue = valueMapper?.[originalValue];

        // Extract base label text (before any parentheses)
        const baseLabel = originalLabel.split("(")[0].trim();
        const updatedLabel = `${baseLabel} (${mappedValue})`;

        // Return the updated rating object
        return {
            ...rating,
            value: mappedValue,
            label: updatedLabel,
            text: updatedLabel
        };
    }

    /**
     * Removes duplicate options based on the 'value' property from the input array.
     * Keeps the last occurrence of each unique value.
     *
     * @param {Array} options - Array of option objects with a 'value' property.
     * @returns {Array} A new array with duplicates removed.
     */
    removeDuplicateOptionsOfExtendedRater(options) {
        var newOptions: any = [];
        if(options.length > 0) {
            var uniqueObject = {};
            for (var i in options) {
                let objTitle = options[i]['value'];
                uniqueObject[objTitle] = options[i];
            }
            for (i in uniqueObject) {
                newOptions.push(uniqueObject[i]);
            }
        }
        return newOptions;
    }

    getDynamicExtendedRaterMapping(main_rater_type) {
        // Get the specific value map for this question and rater type
        return this.extendedRaterMapper[main_rater_type][this.extendedRaterType];
    }

    /**
     * Maps an array of values to their corresponding labels using the provided filter's options.
     *
     * @param {Array} values - The array of values to map.
     * @param {Object} filter - The filter object containing originalPossibleValues.
     * @returns {Object} An object containing:
     *   - mappedNewValueText: A comma-separated string of the corresponding labels.
     *   - mappedNewValues: The original values array.
     *   - mappedNewLabels: An array of corresponding labels.
     */
    getExtendedRaterTextValues(values, filter, applySavedFilter?) {
        let str = "";
        let new_labels: any = [];
        if (applySavedFilter) {
            var options = filter?.possibleValues;
        } else {
            var options = filter?.originalPossibleValues;
        }
        console.log(options)
        for(let i in values) {
            str = str + this.getExtendedRaterText(options, values[i]) + ', ';
            new_labels.push(this.getExtendedRaterText(options, values[i]));
        }
        if(str.length > 0) {
            str = str.substring(0, str.length - 2)
        }
        return {
            mappedNewValueText: str,
            mappedNewValues: values,
            mappedNewLabels: new_labels
        };
    }
    
    /**
     * Returns the label associated with the given value from an array of option objects.
     * Each option object is expected to have 'value' and 'label' properties.
     *
     * @param {Array} options - Array of option objects.
     * @param {*} value - The value to search for.
     * @returns {string|undefined} - The label of the matching option, or undefined if not found.
     */
    getExtendedRaterText(options, value) {
        const match = options.find(option => option.value === value);
        return match?.label;
    }


}